import { VectorColorConstantReference } from './PropertyWrappers.js';
import { uuidv4 } from '../../uuidv4.js';
import { Constants as BABYLON_Constants } from '@inter-ikea-betrakta-babylonjs/core/Engines/constants.js';
/**
 * The ViewerMaterialWrapper handles all the logic for implementing out material interfaces
 * and translating the that to the underlying ViewerMaterial.
 * It would be possible to have ViewerMaterial implement the interfaces directly but
 * That lead to much messier code, so this setup was chosen.
 */
export class ViewerMaterialWrapper {
    constructor(material, is_applied_by_model_loader, asset_manager, _mark_scene_dirty) {
        this.is_applied_by_model_loader = is_applied_by_model_loader;
        this._mark_scene_dirty = _mark_scene_dirty;
        this._asset_manager = asset_manager;
        this._material = material;
        this._viewer_id = uuidv4();
        this._base_color_factor = new VectorColorConstantReference(material, 'albedoColor', _mark_scene_dirty);
        this._emissive_factor = new VectorColorConstantReference(material, 'emissiveColor', _mark_scene_dirty);
        if (this._material.bumpTexture)
            asset_manager.init_texture(this._material.bumpTexture);
        if (this._material.albedoTexture)
            asset_manager.init_texture(this._material.albedoTexture);
        if (this._material.metallicTexture)
            asset_manager.init_texture(this._material.metallicTexture);
        if (this._material.lightmapTexture)
            asset_manager.init_texture(this._material.lightmapTexture);
    }
    // Wrapper properties
    get babylon_material() {
        return this._material;
    }
    get viewer_id() {
        return this._viewer_id;
    }
    get name() {
        return this._material.name;
    }
    // Start external properties
    // IMaterial properties
    get id() {
        return this._viewer_id;
    }
    get unlit() {
        return this._material.unlit;
    }
    set unlit(val) {
        this._material.unlit = true;
    }
    get metallic() {
        // We always use metallic roughness materials.
        return this._material.metallic;
    }
    set metallic(val) {
        this._material.metallic = val;
    }
    get roughness() {
        // We always use metallic roughness materials.
        return this._material.roughness;
    }
    set roughness(val) {
        this._material.roughness = val;
    }
    get opacity() {
        return this._material.alpha;
    }
    set opacity(val) {
        if (val < 1.0) {
            this._material.alphaMode = BABYLON_Constants.ALPHA_COMBINE;
        }
        else {
            this._material.alphaMode = BABYLON_Constants.ALPHA_DISABLE;
        }
        this._material.alpha = val;
    }
    get base_color_factor() {
        return this._base_color_factor;
    }
    set base_color_factor(val) {
        this._base_color_factor.x = val.x;
        this._base_color_factor.y = val.y;
        this._base_color_factor.z = val.z;
    }
    get base_color_texture() {
        var _a, _b;
        if (this._material.albedoTexture !== null) {
            return (_b = (_a = this._asset_manager) === null || _a === void 0 ? void 0 : _a.textures.find_id(this._material.albedoTexture)) !== null && _b !== void 0 ? _b : null;
        }
        return null;
    }
    set base_color_texture(val) {
        var _a;
        if (val === null) {
            this._material.albedoTexture = val;
            return;
        }
        const texture = (_a = this._asset_manager) === null || _a === void 0 ? void 0 : _a.textures.get(val);
        if (texture !== undefined) {
            this._material.albedoTexture = texture;
        }
        else {
            throw new Error('invalid texture id');
        }
    }
    get emissive_factor() {
        return this._emissive_factor;
    }
    set emissive_factor(val) {
        this._emissive_factor.x = val.x;
        this._emissive_factor.y = val.y;
        this._emissive_factor.z = val.z;
    }
    get emissive_strength() {
        return this._material.emissiveIntensity;
    }
    set emissive_strength(val) {
        if (this._material.emissiveIntensity !== val) {
            this._material.emissiveIntensity = val;
            this._mark_scene_dirty();
        }
    }
    get emissive_texture() {
        var _a, _b;
        if (this._material.emissiveTexture !== null) {
            return (_b = (_a = this._asset_manager) === null || _a === void 0 ? void 0 : _a.textures.find_id(this._material.emissiveTexture)) !== null && _b !== void 0 ? _b : null;
        }
        return null;
    }
    set emissive_texture(val) {
        var _a;
        if (val === null) {
            this._material.emissiveTexture = val;
            return;
        }
        const texture = (_a = this._asset_manager) === null || _a === void 0 ? void 0 : _a.textures.get(val);
        if (texture !== undefined) {
            this._material.emissiveTexture = texture;
        }
        else {
            throw new Error('invalid texture id');
        }
    }
    get normal_texture() {
        var _a, _b;
        if (this._material.bumpTexture !== null) {
            return (_b = (_a = this._asset_manager) === null || _a === void 0 ? void 0 : _a.textures.find_id(this._material.bumpTexture)) !== null && _b !== void 0 ? _b : null;
        }
        return null;
    }
    set normal_texture(val) {
        var _a;
        if (val === null) {
            this._material.bumpTexture = val;
            return;
        }
        const texture = (_a = this._asset_manager) === null || _a === void 0 ? void 0 : _a.textures.get(val);
        if (texture !== undefined) {
            this._material.bumpTexture = texture;
        }
        else {
            throw new Error('invalid texture id');
        }
    }
    get occlusion_roughness_metallic_texture() {
        var _a, _b;
        if (this._material.metallicTexture !== null) {
            return (_b = (_a = this._asset_manager) === null || _a === void 0 ? void 0 : _a.textures.find_id(this._material.metallicTexture)) !== null && _b !== void 0 ? _b : null;
        }
        return null;
    }
    set occlusion_roughness_metallic_texture(val) {
        var _a;
        if (val === null) {
            this._material.metallicTexture = val;
            return;
        }
        const texture = (_a = this._asset_manager) === null || _a === void 0 ? void 0 : _a.textures.get(val);
        if (texture !== undefined) {
            this._material.metallicTexture = texture;
        }
        else {
            throw new Error('invalid texture id');
        }
    }
    // IAdvanedMaterial properties
    get reflection_texture() {
        var _a, _b;
        if (this._material.reflectionTexture !== null) {
            return (_b = (_a = this._asset_manager) === null || _a === void 0 ? void 0 : _a.textures.find_id(this._material.reflectionTexture)) !== null && _b !== void 0 ? _b : null;
        }
        return null;
    }
    set reflection_texture(val) {
        var _a;
        if (val === null) {
            this._material.reflectionTexture = val;
            return;
        }
        const texture = (_a = this._asset_manager) === null || _a === void 0 ? void 0 : _a.textures.get(val);
        if (texture !== undefined) {
            this._material.reflectionTexture = texture;
        }
        else {
            throw new Error('invalid texture id');
        }
    }
    get lightmap_texture() {
        var _a, _b;
        if (this._material.lightmapTexture !== null) {
            return (_b = (_a = this._asset_manager) === null || _a === void 0 ? void 0 : _a.textures.find_id(this._material.lightmapTexture)) !== null && _b !== void 0 ? _b : null;
        }
        return null;
    }
    set lightmap_texture(val) {
        var _a;
        if (val === null) {
            this._material.lightmapTexture = val;
            return;
        }
        const texture = (_a = this._asset_manager) === null || _a === void 0 ? void 0 : _a.textures.get(val);
        if (texture !== undefined) {
            this._material.lightmapTexture = texture;
        }
        else {
            throw new Error('invalid texture id');
        }
    }
    // End exposed properties
    clone(name_remapper = (s) => s) {
        return new ViewerMaterialWrapper(this._material.clone(name_remapper(this._material.name)), false, this._asset_manager, this._mark_scene_dirty);
    }
    edit() {
        return this;
    }
    dispose() {
        this._material.dispose();
        // Make sure that the material can get collected
        // @ts-ignore
        this._material = null;
    }
}
//# sourceMappingURL=ViewerMaterialWrapper.js.map